import { useState } from "react";
import { useTranslation } from "@coworker/locales";
import { ViewerFragmentCommonProps } from "../ViewerFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import {
  MediaOrderedDetails,
  ItemNoAndType,
} from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import { TitledBoxedTextWithModal } from "../../../TitledBoxedTextWithModal/TitledBoxedTextWithModal";
import { MediasCompletableOrderedDetailsModalContent } from "../../../MediasCompletableOrderedDetailsModalContent/MediasCompletableOrderedDetailsModalContent";
import { useMediasAndArticleCount } from "@coworker/app/src/components/RoomSettings/hooks/useMediasAndArticleCount";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  shouldShowField,
  VisibilityCondition,
} from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import {
  groupMediasByType,
  getUniqueMediaCount,
} from "@coworker/apprestructured/src/task-management/helpers/media.helper";
import LoadingViewerFragment from "../LoadingViewerFragment/LoadingViewerFragment";

export type MediasPrintPtagsViewerFragmentConfigProps = {
  field: string;
  title: string;
  subTitle?: string;
  visibility: VisibilityCondition;
};

type MediasPrintPtagsViewerFragmentProps = {
  configProps: MediasPrintPtagsViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
};

const MediasPrintPtagsViewerFragment = ({
  configProps,
  commonProps,
}: MediasPrintPtagsViewerFragmentProps) => {
  const { field, title, subTitle, visibility } = configProps;
  const { task, onTaskChange, blockInput } = commonProps;
  const { t } = useTranslation();
  const storeId = useStoreId();

  const [localDetails, setLocalDetails] = useState<MediaOrderedDetails[]>([]);

  const taskIsLoading = isLoading(task);

  const mediasOrderedDetails = taskIsLoading
    ? null
    : (getByPath(task, field) as MediaOrderedDetails[] | null);

  const itemNoAndType = taskIsLoading
    ? null
    : (getByPath(task, "details.item") as ItemNoAndType | null);

  const { data: mediasAndArticleCount } = useMediasAndArticleCount(
    itemNoAndType?.no,
    storeId
  );

  if (taskIsLoading) {
    return <LoadingViewerFragment />;
  }

  const isVisible = shouldShowField(task as Record<string, any>, visibility);

  if (!isVisible) {
    return null;
  }

  if (!itemNoAndType) {
    return <LoadingViewerFragment />;
  }

  const hasMedias =
    Array.isArray(mediasOrderedDetails) && mediasOrderedDetails.length > 0;
  const orderedDetails = hasMedias ? mediasOrderedDetails : [];

  const totalQuantity = orderedDetails.reduce(
    (acc, media) => acc + (media.ptags_ordered?.quantity ?? 0),
    0
  );
  const uniqueMediaCount = getUniqueMediaCount(orderedDetails);
  const quantityText = t("ptagsCountString", { count: totalQuantity });
  const mediaText = t("mediaCountString", { count: uniqueMediaCount });
  const summary = `${quantityText} ${t("inString").toLowerCase()} ${mediaText}`;
  const modalTitle = t("mediaCountString", { count: uniqueMediaCount });

  const matchedMedias: MediasAndArticleCountResponse[] =
    mediasAndArticleCount?.filter((media) =>
      orderedDetails.some(
        (m) => m.id === media.mediaId && m.article_id === media.articleId
      )
    ) ?? [];

  const groupedMedias = groupMediasByType(matchedMedias);

  const handleModalOpen = () => {
    setLocalDetails(
      orderedDetails.map((m) => ({
        ...m,
        pieces_ordered: { ...m.pieces_ordered },
        ptags_ordered: { ...m.ptags_ordered },
      }))
    );
  };

  const handleTogglePiecesCompleted = (mediaId: string, articleId: string) => {
    setLocalDetails((prev) =>
      prev.map((media) =>
        media.id === mediaId && media.article_id === articleId
          ? {
              ...media,
              pieces_ordered: {
                ...media.pieces_ordered,
                completed: !media.pieces_ordered.completed,
              },
            }
          : media
      )
    );
  };

  const handleTogglePtagsCompleted = (mediaId: string, articleId: string) => {
    setLocalDetails((prev) =>
      prev.map((media) =>
        media.id === mediaId && media.article_id === articleId
          ? {
              ...media,
              ptags_ordered: {
                ...media.ptags_ordered,
                completed: !media.ptags_ordered.completed,
              },
            }
          : media
      )
    );
  };

  const handleSave = () => {
    onTaskChange(setByPath(field, localDetails));
  };

  return (
    <TitledBoxedTextWithModal
      title={t(title)}
      subTitle={subTitle ? t(subTitle) : undefined}
      listItemTitle={summary}
      testId="medias-print-ptags-viewer-fragment"
      disabled={blockInput || !hasMedias}
      modalTitle={modalTitle}
      onModalOpen={handleModalOpen}
      modalContent={
        <MediasCompletableOrderedDetailsModalContent
          itemNoAndType={itemNoAndType}
          groupedMedias={groupedMedias}
          mediasOrderedDetails={localDetails}
          onTogglePiecesCompleted={handleTogglePiecesCompleted}
          onTogglePtagsCompleted={handleTogglePtagsCompleted}
        />
      }
      onSave={handleSave}
    />
  );
};

export default MediasPrintPtagsViewerFragment;
